import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  TimeScale,
  Tooltip,
  Legend,
} from "chart.js";
import { format } from "date-fns";
import React, { useRef } from "react";
import { Box } from "@material-ui/core";

ChartJS.register(LinearScale, PointElement, TimeScale, Tooltip, Legend);

const COLORS = [
  "#009E73", // Teal green
  "#0072B2", // Blue
  "#D55E00", // Vermillion
  "#CC79A7", // Reddish purple
  "#F0E442", // Yellow
  "#56B4E9", // Sky blue
];

const annotatedLinePlugin = {
  id: "annotatedVerticalLine",
  afterDraw(chart) {
    const active = chart.tooltip?.getActiveElements?.();
    if (!active?.length) return;
    const x = active[0].element.x;
    const yScale = Object.values(chart.scales).find((s) => s.axis === "y");
    if (!yScale) return;
    const ctx = chart.ctx;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, yScale.top);
    ctx.lineTo(x, yScale.bottom);
    ctx.lineWidth = 9;
    ctx.strokeStyle = "rgba(0, 0, 255, 0.2)";
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x, yScale.top);
    ctx.lineTo(x, yScale.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(0, 0, 255, 0.4)";
    ctx.stroke();
    ctx.restore();
  },
};

export default function DrilldownScatterChart({
  seriesByLoc,
  unit,
  parameter,
  season,
  height = 450,
}) {
  const chartRef = useRef();
  const data = {
    datasets: Object.entries(seriesByLoc).map(([locParam, pts], i) => ({
      label: locParam,
      data: pts.map((d) => ({
        x: d.date,
        y: d.value,
        locationParam: locParam,
      })),
      backgroundColor: COLORS[i % COLORS.length],
      pointRadius: 5,
    })),
  };

  const options = {
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      axis: "x",
      intersect: false,
    },
    plugins: {
      tooltip: {
        mode: "index",
        axis: "x",
        intersect: false,
        callbacks: {
          title: (items) => {
            const date = items[0].raw.x;
            return format(date, "MMM dd, yyyy");
          },
          label: (ti) => {
            const val = ti.raw.y.toFixed(1);
            const locParam = ti.raw.locationParam;
            return `${val} ${unit} | ${locParam}, ${parameter}`;
          },
        },
        backgroundColor: "#fff",
        titleColor: "#333",
        bodyColor: "#333",
        borderColor: "#999",
        borderWidth: 1,
        cornerRadius: 6,
        padding: 10,
        titleFont: { weight: "bold", size: 14 },
        bodyFont: { size: 14 },
      },
      legend: {
        position: "top",
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: season === "summer" ? "day" : "month",
          displayFormats: {
            day: "MM/DD/YY", // e.g., 07/24
            month: "MM/YY", // e.g., 24
          },
        },
        ticks: {
          autoSkip: true,
        },
      },
    },
  };

  return (
    <Box height={height} width="100%">
      <Scatter
        ref={chartRef}
        data={data}
        options={options}
        plugins={[annotatedLinePlugin]}
      />
    </Box>
  );
}
